<template>
    <div>
        <v-dialog v-model="internalValue">
            <v-card class="stock-media-card">
                <div class="d-flex flex-column flex-grow-1">
                    <div class="d-flex justify-space-between p-3">
                        <v-btn-toggle dense v-model="mediaType">
                            <v-btn value="photo" class="no-text-transform"> Photo </v-btn>

                            <v-btn value="video" class="no-text-transform"> Video </v-btn>
                        </v-btn-toggle>

                        <TextButtonInput
                            v-model="searchText"
                            :readonly="false"
                            @submit="searchPexels(searchText, mediaType)"
                        >
                            <template #action>
                                <v-btn class="no-text-transform" depressed @click="searchPexels(searchText, mediaType)">
                                    Search</v-btn
                                >
                            </template>
                        </TextButtonInput>
                        <v-btn class="no-text-transform" @click="internalValue = false" depressed dark>Close</v-btn>
                    </div>

                    <div v-show="mediaType == 'photo'" ref="scrollContainer" class="main-section">
                        <div class="media-grid">
                            <div class="media-result" v-for="result in photos.results">
                                <img class="media-img" v-if="result.src.medium" :src="result.src.medium" />

                                <v-overlay class="overlay" absolute attach :value="true">
                                    <v-btn
                                        class="no-text-transform"
                                        @click="initImport(result.src.original)"
                                        color="success"
                                    >
                                        Import
                                    </v-btn>
                                </v-overlay>
                            </div>
                        </div>

                        <div v-if="photos.nextPage" v-intersect="onIntersect" class="d-flex justify-center p-4">
                            <v-btn
                                class="no-text-transform"
                                depressed
                                :loading="loading"
                                :disabled="loading || !photos.nextPage"
                                >Load More</v-btn
                            >
                        </div>
                    </div>

                    <div v-show="mediaType == 'video'" ref="scrollContainer" class="main-section">
                        <div class="media-grid">
                            <div class="media-result" v-for="result in videos.results">
                                <video
                                    :ref="`video-${result.id}`"
                                    class="media-video"
                                    playsinline
                                    loop
                                    muted
                                    preload="none"
                                    :poster="result.image"
                                ></video>

                                <div class="duration-tag">
                                    {{ result.duration | formatTimeStamp }}
                                </div>
                                <v-overlay class="overlay" absolute attach :value="true">
                                    <div class="d-flex flex-column" style="gap: 12px">
                                        <v-btn class="no-text-transform" @click="initPreview(result)" color="primary">
                                            Preview
                                        </v-btn>
                                        <v-btn
                                            class="no-text-transform"
                                            @click="initVideoImport(result)"
                                            color="success"
                                        >
                                            Import
                                        </v-btn>
                                    </div>
                                </v-overlay>
                            </div>
                        </div>

                        <div v-if="videos.nextPage" v-intersect="onIntersect" class="d-flex justify-center p-4">
                            <v-btn
                                class="no-text-transform"
                                depressed
                                :loading="loading"
                                :disabled="loading || !videos.nextPage"
                                >Load More</v-btn
                            >
                        </div>
                    </div>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="downloadingImport" max-width="400px">
            <v-card>
                <v-card-title> Downloading </v-card-title>
                <v-card-text>
                    <v-progress-linear
                        rounded
                        v-model="downloadImportProgress"
                        :color="downloadImportProgress === 100 ? 'success' : 'primary'"
                        dark
                        height="20"
                        >{{ downloadImportProgress }}%</v-progress-linear
                    >
                </v-card-text>
                <v-card-actions class="d-flex justify-center">
                    <v-btn @click="cancelDownload" depressed color="error" class="no-text-transform">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog @click:outside="resetPreview" v-model="previewModal" v-show="selectedPreviewUrl" max-width="700">
            <v-card class="d-flex justify-center" style="position: relative; background-color: black">
                <video
                    ref="previewPlayer"
                    style="width: 100%"
                    :src="selectedPreviewUrl"
                    class="media-video"
                    playsinline
                    loop
                    controls
                ></video>

                <v-btn @click="resetPreview" fab x-small style="position: absolute; top: 20px; right: 20px">
                    <font-awesome-icon icon="fa-solid fa-x"></font-awesome-icon>
                </v-btn>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions } from 'vuex';
import moment from 'moment';
import SlideUpload from '../SlideUpload.vue';
import TextButtonInput from '@/components/ui/TextButtonInput.vue';

export default {
    data() {
        return {
            visible: this.value,
            searchText: null,
            page: 2,
            perPage: 20,
            loading: true,
            photos: {
                results: [],
                next_page: null,
            },
            videos: {
                results: [],
                next_page: null,
            },
            mediaType: 'photo',
            downloadingImport: false,
            downloadImportProgress: 0,
            cancelTokenSource: null,
            selectedPreviewUrl: null,
            previewModal: false,
            fallbackSearchText: 'landscape',
        };
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    components: { SlideUpload, TextButtonInput },
    computed: {
        internalValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        eventId: {
            get() {
                return this.$store.state.tributeVideo.eventId;
            },
        },
    },
    watch: {
        //Disable debounced search for now,
        //200 req per hour
        //20k per month
        // searchText(newVal) {
        //     console.log(newVal, 'searchtext updated')
        //     this.debouncedSearch(newVal);
        // }
        mediaType(newVal) {
            if (newVal === 'photo' && !this.photos.results.length) {
                this.searchPexels(this.fallbackSearchText, newVal);
            } else if (newVal === 'video' && !this.videos.results.length) {
                this.searchPexels(this.fallbackSearchText, newVal);
            }
        },
        internalValue(newVal) {
            if (!newVal) return;

            this.searchPexels(this.fallbackSearchText, this.mediaType);
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');

            return `${formattedMinutes}:${formattedSeconds}`;
        },
    },
    methods: {
        ...mapActions(['showSnackbar']),
        initPreview(video) {
            this.selectedPreviewUrl = video?.video_files[0].link;

            if (!this.selectedPreviewUrl) {
                this.showSnackbar({ message: 'Unable to find preview source', color: 'error' });
                return;
            }

            this.selectedVideo = video;
            this.previewModal = true;

            this.$nextTick(() => {
                const previewPlayer = this.$refs.previewPlayer;

                if (previewPlayer) {
                    previewPlayer.play();
                }
            });
        },
        resetPreview() {
            this.previewModal = false;
            this.selectedPreviewUrl = null;
        },
        onIntersect(entries) {
            if (entries[0].isIntersecting) {
                this.fetchPexelsNextPage();
            }
        },
        scrollToTop() {
            const el = this.$refs.scrollContainer;
            if (el) el.scrollTop = 0;
        },
        // async getPexelsCuratedPhotos() {

        //     try {
        //         this.loading = true;
        //         this.scrollToTop();

        //         const resp = await this.axios({
        //             method: 'get',
        //             url: process.env.VUE_APP_PEXELS_API_BASE + 'curated',
        //             params: {
        //                 per_page: this.perPage, // optional: control number of results
        //                 page: this.page,
        //             },
        //             headers: {
        //                 Authorization: process.env.VUE_APP_PEXELS_API_KEY,
        //             },
        //         });

        //         if (resp.data.photos) {
        //             this.photos.results = resp.data.photos;
        //             this.photos.nextPage = resp.data.next_page;
        //         }
        //     } catch (error) {
        //         console.log(error, 'error fetching from pexels');
        //     } finally {
        //         this.loading = false;
        //     }
        // },
        async searchPexels(text, mediaType) {
            if (!text) text = this.fallbackSearchText;

            const isVideo = mediaType === 'video';

            try {
                this.lading = true;
                this.scrollToTop();

                const endpointMod = isVideo ? 'videos/' : '';

                const resp = await this.axios({
                    method: 'get',
                    url: process.env.VUE_APP_PEXELS_API_BASE + endpointMod + 'search',
                    params: {
                        query: text,
                        per_page: this.perPage,
                        page: this.page,
                        orientation: 'landscape',
                    },
                    headers: {
                        Authorization: process.env.VUE_APP_PEXELS_API_KEY,
                    },
                });

                if (isVideo) {
                    if (resp.data.videos) {
                        this.videos.results = resp.data.videos;
                        this.videos.nextPage = resp.data.next_page;
                    }
                } else {
                    if (resp.data.photos) {
                        this.photos.results = resp.data.photos;
                        this.photos.nextPage = resp.data.next_page;
                    }
                }
            } catch (error) {
                console.log(error, 'error fetching from pexels');
            } finally {
                this.loading = false;
            }
        },
        cancelDownload() {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel('User canceled download');
            }
            this.downloadingImport = false;
        },
        async fetchPexelsNextPage() {
            const isVideo = this.mediaType === 'video';

            if (isVideo && !this.videos.nextPage) {
                return;
            }

            if (!this.photos.nextPage) {
                return;
            }

            const endpoint = isVideo ? this.videos.nextPage : this.photos.nextPage;

            try {
                this.loading = true;

                const resp = await this.axios({
                    method: 'get',
                    url: endpoint,
                    headers: {
                        Authorization: process.env.VUE_APP_PEXELS_API_KEY,
                    },
                });

                if (isVideo) {
                    if (resp.data.videos) {
                        this.videos.results = this.videos.results.concat(resp.data.videos);
                        this.videos.nextPage = resp.data.next_page;
                    }
                } else {
                    if (resp.data.photos) {
                        this.photos.results = this.photos.results.concat(resp.data.photos);
                        this.photos.nextPage = resp.data.next_page;
                    }
                }
            } catch (error) {
                console.log(error, 'error fetching nex page');
            } finally {
                this.loading = false;
            }
        },
        initVideoImport(video) {
            const file = video?.video_files[0];

            if (!file.link) {
                throw new Error('No source found for import link');
            }

            this.initImport(file.link);
        },
        async initImport(url) {
            const falbackType = this.mediaType === 'photo' ? 'image/jpeg' : 'video/mp4';
            const file = await this.fetchAsFile(url, falbackType);

            if (this.eventId) {
                this.$emit('init-import', file);
                this.internalValue = false;
            }
        },
        async fetchAsFile(url, fallbackType = 'application/octet-stream') {
            try {
                this.downloadingImport = true;
                this.cancelTokenSource = this.axios.CancelToken.source();

                const blob = await this.fetchUrlData(
                    url,
                    progress => {
                        this.downloadImportProgress = Math.floor(progress);
                    },
                    this.cancelTokenSource.token,
                );

                const contentType = blob.type || fallbackType;
                const filename = this.parseFilenameFromUrl(url);

                return new File([blob], filename, { type: contentType });
            } catch (error) {
            } finally {
                setTimeout(() => {
                    this.downloadingImport = false;
                    this.downloadImportProgress = 0;
                }, 500);
            }
        },

        async fetchUrlData(url, onProgress, cancelToken) {
            const response = await this.axios({
                method: 'get',
                url,
                responseType: 'blob',
                cancelToken,
                onDownloadProgress: progressEvent => {
                    if (progressEvent.lengthComputable && typeof onProgress === 'function') {
                        const percent = (progressEvent.loaded / progressEvent.total) * 100;
                        onProgress(percent.toFixed(1));
                    }
                },
            });

            return response.data;
        },
        parseFilenameFromUrl(url) {
            try {
                const cleaned = this.cleanTrailingSlash(url);
                return cleaned.split('/').pop()?.split('?')[0] || 'image.jpg';
            } catch (error) {
                console.error('Unable to parse filename', error);
                return 'image.jpg';
            }
        },
        cleanTrailingSlash(url) {
            let cleaned = url.replace(/\/$/, '');
            return cleaned;
        },
    },
    created() {
        this.debouncedSearch = debounce(this.searchPexels, 500);
    },
};
</script>
<style lang="scss" scoped>
video {
    display: block;
    max-height: 800px;
}

.media-grid {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: start;
    overflow: auto;
}
.media-result {
    position: relative !important;
    overflow: hidden;

    img,
    video {
        width: 100%;
        border-radius: 8px;
        display: block;
    }

    video {
        max-height: 250px;
    }

    .duration-tag {
        border: 1px solid $medium-gray;
        position: absolute;
        bottom: 5px;
        right: 5px;
        background-color: white;
        border-radius: 4px;
        padding: 0 8px;
        font-size: 12px;
    }
}

.stock-media-card {
    height: 90vh;
    display: flex;
}

.main-section {
    flex-grow: 1;
    overflow: auto;
    padding: 14px;
}

.overlay {
    opacity: 0;
    transition: 0.2s;
    border-radius: 8px;
}

.media-result:hover .overlay {
    opacity: 1;
}
</style>
